import React, { useState } from 'react';
import { useClientPortal } from '../contexts/ClientPortalContext';
import ProductCard from './ProductCard';
import StockIndicator from './StockIndicator';

const AdaptiveProductCard = ({ product, highlight = false }) => {
    const { orderStarted, cart, addToCart } = useClientPortal();
    const [quantity, setQuantity] = useState(1);
    const [showQuantityInput, setShowQuantityInput] = useState(false);

    // Utiliser item_id (Zoho) comme ID unique, avec fallback sur id
    const productId = product.item_id || product.id;
    const existingCartItem = cart.find(item => {
        const itemProductId = item.product.item_id || item.product.id;
        return itemProductId === productId;
    });

    // Récupérer l'ETA pour l'indicateur de stock
    const etaValue = product.cf_in_production_1 || 
                    product.custom_field_hash?.cf_in_production_1 || '';

    // Vérifier si le produit est discontinué
    const isDiscontinuedValue = product.cf_tbd || 
                               product.custom_field_hash?.cf_tbd || false;

    const isProductDiscontinued = isDiscontinuedValue === true || isDiscontinuedValue === 'true';

    const handleAddToCart = () => {
        if (!orderStarted) return;
        
        if (showQuantityInput) {
            addToCart(product, quantity);
            setShowQuantityInput(false);
            setQuantity(1);
        } else {
            setShowQuantityInput(true);
        }
    };

    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value) || 1;
        setQuantity(Math.max(1, value));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddToCart();
        } else if (e.key === 'Escape') {
            setShowQuantityInput(false);
            setQuantity(1);
        }
    };

    if (!orderStarted) {
        // Mode normal - juste affichage avec possibilité d'animation
        return (
            <div style={{
                animation: highlight ? 'highlight-animation 3s ease-in-out' : 'none',
                borderRadius: '8px'
            }}>
                <ProductCard product={product} />
                {highlight && (
                    <style>{`
                        @keyframes highlight-animation {
                            0%, 100% { 
                                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                                transform: scale(1);
                            }
                            25%, 75% { 
                                box-shadow: 0 8px 25px rgba(255, 193, 7, 0.5);
                                transform: scale(1.02);
                            }
                            50% { 
                                box-shadow: 0 12px 35px rgba(255, 193, 7, 0.7);
                                transform: scale(1.05);
                            }
                        }
                    `}</style>
                )}
            </div>
        );
    }

    // Mode commande - interface de sélection
    return (
        <div className="product-card" style={{
            border: existingCartItem ? '3px solid #4CAF50' : '1px solid #ddd',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        }}>
            {existingCartItem && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: '#4CAF50',
                    color: 'white',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 1
                }}>
                    {existingCartItem.quantity}
                </div>
            )}

            <div className="product-image">
                <img
                    src={product.image || (product.image_name ? `/api/zoho/item/${product.item_id}/image` : "/images/placeholder.png")}
                    alt={product.name}
                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                />
            </div>

            <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-reference">SKU: {product.sku || product.default_code || 'N/A'}</p>
                <StockIndicator stockLevel={product.stock_on_hand !== undefined ? product.stock_on_hand : (product.available_stock || 0)} etaValue={etaValue} />

                {/* Badge discontinued item - affiché après l'indicateur de stock */}
                {isProductDiscontinued && (
                    <div className="discontinued-indicator">
                        <span className="discontinued-badge">DISCONTINUED ITEM</span>
                    </div>
                )}

                {/* Interface de commande */}
                <div style={{ marginTop: '15px' }}>
                    {!showQuantityInput ? (
                        <button
                            onClick={handleAddToCart}
                            style={{
                                width: '100%',
                                padding: '10px',
                                background: existingCartItem ? '#4CAF50' : 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'transform 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            {existingCartItem ? `✅ In Cart (${existingCartItem.quantity})` : '🛒 Add to Cart'}
                        </button>
                    ) : (
                        <div style={{ 
                            display: 'flex', 
                            gap: '4px', 
                            alignItems: 'stretch'
                        }}>
                            <input
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={handleQuantityChange}
                                onKeyDown={handleKeyPress}
                                autoFocus
                                style={{
                                    width: '50px',
                                    padding: '8px 4px',
                                    border: '2px solid #2196F3',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}
                            />
                            <button
                                onClick={handleAddToCart}
                                style={{
                                    flex: 1,
                                    padding: '8px',
                                    background: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                ✓ Add
                            </button>
                            <button
                                onClick={() => {
                                    setShowQuantityInput(false);
                                    setQuantity(1);
                                }}
                                style={{
                                    padding: '8px',
                                    background: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    minWidth: '32px'
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdaptiveProductCard;
